import React from "react";
import { useTranslation } from "react-i18next";
import { Backdrop, Fade, Modal } from "@material-ui/core";
import { useStyles } from "./PaymentCardTile.styles";
import { PaymentToken } from "../../../pages/BankCardsPage/BankCardsTypes";
import { ReactComponent as CloseIcon } from "../../../assets/cross.svg";
import Checkbox from "../../common/checkbox/checkbox";
import { getCardType } from "../../../shared/cardTypes";

interface PaymentCardTileProps {
  paymentToken: PaymentToken;
  onRemove: () => void;
  isRemoving: boolean;
}

const SWIPE_REVEAL_WIDTH = 96;
const SWIPE_THRESHOLD = 40;

const formatExpiration = (month: number, year: number) => {
  const mm = String(month).padStart(2, "0");
  const yy = String(year).slice(-2);
  return `${mm}/${yy}`;
};

const PaymentCardTile: React.FC<PaymentCardTileProps> = ({
  paymentToken,
  onRemove,
  isRemoving,
}) => {
  const { t } = useTranslation();
  const dir = t("css.side") as "rtl" | "ltr";
  const classes = useStyles();

  const IssuerLogo = getCardType(paymentToken.issuer).Logo;

  const [revealAmount, setRevealAmount] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const touchStartXRef = React.useRef<number | null>(null);
  const revealStartRef = React.useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    revealStartRef.current = revealAmount;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const rawDelta = e.touches[0].clientX - touchStartXRef.current;
    const revealDelta = dir === "rtl" ? rawDelta : -rawDelta;
    const next = Math.min(
      SWIPE_REVEAL_WIDTH,
      Math.max(0, revealStartRef.current + revealDelta)
    );
    setRevealAmount(next);
  };

  const handleTouchEnd = () => {
    touchStartXRef.current = null;
    setIsDragging(false);
    setRevealAmount((current) =>
      current > SWIPE_THRESHOLD ? SWIPE_REVEAL_WIDTH : 0
    );
  };

  const translateX = (dir === "rtl" ? 1 : -1) * revealAmount;

  const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState(false);

  const openRemoveModal = () => setIsRemoveModalOpen(true);
  const closeRemoveModal = () => setIsRemoveModalOpen(false);

  // Close automatically once an in-flight removal finishes (success or failure).
  const wasRemovingRef = React.useRef(false);
  React.useEffect(() => {
    if (wasRemovingRef.current && !isRemoving) {
      setIsRemoveModalOpen(false);
    }
    wasRemovingRef.current = isRemoving;
  }, [isRemoving]);

  const defaultCheckbox = (
    <Checkbox
      value={t("account.defaultCard")}
      isChecked={false}
      disabled
      facetValue={false}
      updateFacetsValues={() => {}}
      removeFacetsValues={() => {}}
    />
  );

  const expirationRow = (
    <div className={classes.footer}>
      <span className={classes.cardHolderName}>
        {t("account.cardHolderMissing")}
      </span>
      <span
        className={
          paymentToken.expired ? classes.expirationExpired : classes.expiration
        }
      >
        {t("account.expires", {
          date: formatExpiration(
            paymentToken.cardExpirationMonth,
            paymentToken.cardExpirationYear
          ),
        })}
      </span>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <div className={classes.reg}>
        <div className={classes.container} dir={dir}>
          <div className={classes.header}>
            <IssuerLogo className={classes.issuerLogo} />
            <button
              type="button"
              className={classes.removeButton}
              onClick={openRemoveModal}
              disabled={isRemoving}
            >
              {isRemoving ? t("account.removingCard") : t("account.removeCard")}
            </button>
          </div>

          <span className={classes.cardNumber}>
            **** **** **** {paymentToken.lastFourDigits}
          </span>

          {expirationRow}

          <div className={classes.defaultRow}>{defaultCheckbox}</div>
        </div>
      </div>

      {/* Mobile */}
      <div className={classes.res}>
        <div className={classes.swipeWrapper} dir={dir}>
          <button
            type="button"
            className={classes.swipeRemoveAction}
            style={{ [dir === "rtl" ? "left" : "right"]: 0 }}
            onClick={openRemoveModal}
            disabled={isRemoving}
          >
            {isRemoving ? t("account.removingCard") : t("account.removeCard")}
          </button>

          <div
            className={classes.mobileContainer}
            style={{
              transform: `translateX(${translateX}px)`,
              transition: isDragging ? "none" : "transform 0.2s ease",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={() => {
              if (revealAmount > 0) setRevealAmount(0);
            }}
          >
            <div className={classes.mobileHeader}>
              <IssuerLogo className={classes.issuerLogo} />
              <div className={classes.defaultRowMobile}>{defaultCheckbox}</div>
            </div>

            <span className={classes.cardNumberMobile}>
              **** **** **** {paymentToken.lastFourDigits}
            </span>

            {expirationRow}
          </div>
        </div>
      </div>

      <Modal
        className={classes.modal}
        open={isRemoveModalOpen}
        onClose={closeRemoveModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={isRemoveModalOpen}>
          <div className={classes.modalContainer} dir={dir}>
            <div className={classes.modalHeader}>
              <div className={classes.modalTitle}>
                {dir === "rtl" ? (
                  <span>? {t("account.removeCardConfirmTitle")}</span>
                ) : (
                  <span>{t("account.removeCardConfirmTitle")} ?</span>
                )}
              </div>
              <div className={classes.modalCloseIcon} onClick={closeRemoveModal}>
                <CloseIcon style={{ width: 24, height: 24 }} />
              </div>
            </div>

            <button
              type="button"
              className={classes.modalConfirmButton}
              onClick={onRemove}
              disabled={isRemoving}
            >
              {isRemoving ? t("account.removingCard") : t("account.removeCard")}
            </button>

            <span className={classes.modalNotNow} onClick={closeRemoveModal}>
              {t("account.notNow")}
            </span>
          </div>
        </Fade>
      </Modal>
    </>
  );
};

export default PaymentCardTile;
